import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { AuthenticationService } from '@core/services';
import { ActivatedRoute, Router } from '@angular/router';
import { UserType } from '@core/interfaces';
import { BusinessRoutePaths } from '@core/configs/route-paths';

@Component({
  selector: 'app-login',
  styleUrls: ['./login.component.css'],
  templateUrl: './login.component.html'
})
export class LoginFormComponent implements OnInit, OnDestroy {

  public formSubmitAttempt = false;
  public showError = false;
  public showEmailNotConfirmedError = false;
  public isSubmitting = false;
  public isResendingConfirmation = false;
  public resendSuccess = false;
  public resendFailed = false;
  public externalErrorKey: string | null = null;
  private readonly unsubscribe$ = new Subject<void>();
  private readonly allowedExternalErrors = new Set([
    'external_login_failed',
    'email_not_provided',
    'account_not_found',
    'business_account_required',
    'login_failed',
    'email_not_confirmed',
    'session_expired'
  ]);

  public form: FormGroup = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.email,
      Validators.maxLength(256)
    ]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(128)
    ]),
    remeberMe: new FormControl(false)
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthenticationService
  ) {
    this.form.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.clearFeedbackState());
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(params => {
        const externalError = params.get('externalError');
        this.externalErrorKey = externalError
          ? (this.allowedExternalErrors.has(externalError) ? externalError : 'external_login_failed')
          : null;
        this.showEmailNotConfirmedError = this.externalErrorKey === 'email_not_confirmed';
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get email() { return this.form.get('email'); }

  get password() { return this.form.get('password'); }

  public submit(): void {
    if (this.isSubmitting) {
      return;
    }

    this.formSubmitAttempt = true;
    if (!this.form.valid) {
      return;
    }

    this.isSubmitting = true;

    this.authService.login(this.form.value)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => this.isSubmitting = false)
      )
      .subscribe({
        next: (data) => {
          if (!data.success) {
            this.showError = true;
            return;
          }

          const returnUrl = this.route.snapshot?.queryParams?.returnUrl;
          const defaultUrl = data.user.userType === UserType.Business ? BusinessRoutePaths.root : '/';
          this.router.navigate([returnUrl || defaultUrl]);
        },
        error: (error) => this.setAuthError(error)
      });
  }

  public signInWithGoogle(): void {
    this.authService.startGoogleAuth('signin', { redirectPath: BusinessRoutePaths.root });
  }

  public resendConfirmationEmail(): void {
    const email = this.email?.value;

    if (!email || this.email?.invalid) {
      this.formSubmitAttempt = true;
      return;
    }

    this.isResendingConfirmation = true;
    this.resendSuccess = false;
    this.resendFailed = false;

    this.authService.sendConfirmationEmail(email)
      .pipe(
        take(1),
        finalize(() => this.isResendingConfirmation = false)
      )
      .subscribe({
        next: () => {
          this.resendSuccess = true;
          this.resendFailed = false;
        },
        error: () => {
          this.resendSuccess = false;
          this.resendFailed = true;
        }
      });
  }

  private setAuthError(error: unknown): void {
    this.showEmailNotConfirmedError = this.isEmailNotConfirmedError(error);
    this.showError = !this.showEmailNotConfirmedError;
  }

  private clearFeedbackState(): void {
    this.showError = false;
    this.showEmailNotConfirmedError = false;
    this.resendSuccess = false;
    this.resendFailed = false;
    this.externalErrorKey = null;
  }

  private isEmailNotConfirmedError(error: unknown): boolean {
    const text = this.extractErrorText(error);

    return (
      text.includes('email_not_confirmed') ||
      text.includes('emailnotconfirmed') ||
      text.includes('email not confirmed') ||
      text.includes('confirm your email')
    );
  }

  private extractErrorText(error: unknown): string {
    if (error == null) {
      return '';
    }

    if (typeof error === 'string') {
      return error.toLowerCase();
    }

    if (Array.isArray(error)) {
      return error.map(item => this.extractErrorText(item)).join(' ').toLowerCase();
    }

    if (typeof error === 'object') {
      try {
        return JSON.stringify(error).toLowerCase();
      } catch {
        return '';
      }
    }

    return String(error).toLowerCase();
  }

}
